import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAtlasViewport } from './useAtlasViewport';
import { useAtlasNodeMotion } from './useAtlasNodeMotion';
import { useAtlasTree } from './hooks/useAtlasTree';
import { useAtlasMarquee } from './hooks/useAtlasMarquee';
import { useAtlasFolderDrag } from './hooks/useAtlasFolderDrag';
import { useAtlasFolderDelete } from './hooks/useAtlasFolderDelete';
import { useAtlasCanvasEvents } from './hooks/useAtlasCanvasEvents';
import { useAtlasFolderActions } from './hooks/useAtlasFolderActions';
import { useAtlasSelection, useAtlasSelectionSync } from './hooks/useAtlasSelection';
import { FOLDER_DRAG, FOLDER_DRAG_IDS, isAtlasUnfiledId } from './constants';
import AtlasToolbar from './components/AtlasToolbar';
import AtlasMinimap from './components/AtlasMinimap';
import AtlasWorld from './components/AtlasWorld';
import AtlasEmpty from './components/AtlasEmpty';
import AtlasContextMenu from './components/AtlasContextMenu';
import DeleteFoldersModal from '../DeleteFoldersModal';
import './Atlas.css';

/**
 * Atlas — pan/zoom family-tree map of Docs folders.
 */
const AtlasView = ({
  organization = null,
  docs = [],
  docsLoading = false,
  onOpenDoc,
  onOpenDocInNewTab,
  onCreateDoc,
  createDoc,
  onBrowseFolder,
  useDemo = false,
}) => {
  const containerRef = useRef(null);
  const nodesRef = useRef([]);
  const minimapApplyRef = useRef(null);
  const {
    pan,
    zoom,
    panRef,
    zoomRef,
    cameraRef,
    gridRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    setPanLive,
    endPanLive,
    fitToBounds,
    resetView,
    ensureRectInView,
    didPanRef,
  } = useAtlasViewport({ containerRef, minimapApplyRef });

  const [nodeHeights, setNodeHeights] = useState(() => new Map());
  const [viewSize, setViewSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const apply = () => setViewSize({ w: el.clientWidth, h: el.clientHeight });
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { expandedId, setExpandedId, ctx, setCtx, selectedIds, setSelectedIds } =
    useAtlasSelection();

  const { nodes, edges, bounds, clusters, peek, treeReady } = useAtlasTree({
    organization,
    docs,
    useDemo,
    expandedId,
    fitToBounds,
    nodeHeights,
  });
  nodesRef.current = nodes;
  useAtlasSelectionSync(nodes, expandedId, setExpandedId, setSelectedIds);

  const handleMeasuredHeights = useCallback((next) => {
    setNodeHeights((prev) => {
      if (!next || next.size === 0) return prev;
      if (prev.size !== next.size) return next;
      for (const [id, h] of next) {
        if (Math.abs((prev.get(id) || 0) - h) > 2) return next;
      }
      return prev;
    });
  }, []);

  const { registerNode, registerEdge, startDrag, moveDrag, endDrag, livePos } =
    useAtlasNodeMotion({
      layoutNodes: nodes,
      edges,
      onMeasuredHeights: handleMeasuredHeights,
      zoomRef,
    });

  const screenToWorld = useCallback((clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const p = panRef.current;
    const z = zoomRef.current || 1;
    return {
      x: (clientX - rect.left - p.x) / z,
      y: (clientY - rect.top - p.y) / z,
    };
  }, [panRef, zoomRef]);

  const {
    selectionBox,
    didMarqueeDragRef,
    startMarquee,
    moveMarquee,
    endMarquee,
  } = useAtlasMarquee({
    containerRef,
    nodesRef,
    screenToWorld,
    setSelectedIds,
    setCtx,
  });

  const {
    dropHint,
    dropTargetId,
    draggingIds,
    didFolderDragRef,
    beginFolderPointer,
    moveFolderPointer,
    endFolderPointer,
    onDragOverFolder,
    onDragLeaveFolder,
    onDropOnFolder,
    onDropOnCanvas,
  } = useAtlasFolderDrag({
    useDemo,
    organization,
    selectedIds,
    screenToWorld,
    startDrag,
    moveDrag,
    endDrag,
    setExpandedId,
    setCtx,
    nodes,
  });

  const { pending, requestDelete, confirmDelete, cancelDelete } =
    useAtlasFolderDelete({
      useDemo,
      organization,
      selectedIds,
      setSelectedIds,
      setExpandedId,
      setCtx,
    });

  const {
    handlePointerDownCanvas,
    handlePointerMoveCanvas,
    handlePointerUpCanvas,
    handleNodeClick,
    handleCanvasClick,
    handleNodeContextMenu,
    handleCanvasContextMenu,
  } = useAtlasCanvasEvents({
    onPointerDown,
    onPointerMove,
    onPointerUp,
    startMarquee,
    moveMarquee,
    endMarquee,
    moveFolderPointer,
    endFolderPointer,
    didPanRef,
    didMarqueeDragRef,
    didFolderDragRef,
    setExpandedId,
    setSelectedIds,
    setCtx,
  });

  const {
    renamingId,
    setRenamingId,
    handleCreateRootFolder,
    handleCreateChild,
    handleCreateDocInFolder,
    handlePasteDoc,
    handleRename,
    handleRenameCommit,
    handleOpenBrowse,
    handleDeleteFromMenu,
  } = useAtlasFolderActions({
    useDemo,
    organization,
    onCreateDoc,
    createDoc,
    onBrowseFolder,
    nodes,
    selectedIds,
    setSelectedIds,
    setExpandedId,
    setCtx,
    ensureRectInView,
    requestDelete,
    setNodeHeights,
  });

  return (
    <div className="atlas">
      <AtlasToolbar
        bounds={bounds}
        fitToBounds={(b, pad) => fitToBounds(b, pad, { animate: true })}
        resetView={() => resetView(bounds, { animate: true })}
        useDemo={useDemo}
        organizationReady={organization?.ready}
        onCreateRootFolder={handleCreateRootFolder}
      />

      <div
        ref={containerRef}
        className={`atlas__canvas${selectionBox ? ' atlas__canvas--selecting' : ''}`}
        onPointerDown={handlePointerDownCanvas}
        onPointerMove={handlePointerMoveCanvas}
        onPointerUp={handlePointerUpCanvas}
        onPointerCancel={handlePointerUpCanvas}
        onClick={handleCanvasClick}
        onContextMenu={handleCanvasContextMenu}
        onDragOver={(e) => {
          const types = e.dataTransfer.types;
          if (types.includes(FOLDER_DRAG) || types.includes(FOLDER_DRAG_IDS)) {
            e.preventDefault();
          }
        }}
        onDrop={onDropOnCanvas}
      >
        <AtlasWorld
          viewW={viewSize.w}
          viewH={viewSize.h}
          pan={pan}
          zoom={zoom}
          cameraRef={cameraRef}
          gridRef={gridRef}
          nodes={nodes}
          edges={edges}
          clusters={clusters}
          peek={peek}
          draggingIds={draggingIds}
          livePos={livePos}
          expandedId={expandedId}
          selectedIds={selectedIds}
          dropHint={dropHint}
          dropTargetId={dropTargetId}
          registerNode={registerNode}
          registerEdge={registerEdge}
          useDemo={useDemo}
          onOpenDoc={onOpenDoc}
          onOpenDocInNewTab={onOpenDocInNewTab}
          onBrowseFolder={handleOpenBrowse}
          onFolderPointerDown={beginFolderPointer}
          onDragOverFolder={onDragOverFolder}
          onDragLeaveFolder={onDragLeaveFolder}
          onDropOnFolder={onDropOnFolder}
          onNodeClick={handleNodeClick}
          onNodeContextMenu={handleNodeContextMenu}
          renamingId={renamingId}
          onRenameCommit={handleRenameCommit}
          onRenameCancel={() => setRenamingId(null)}
        />

        {selectionBox && (
          <div
            className="atlas__selection-box"
            aria-hidden
            style={{
              left: selectionBox.x,
              top: selectionBox.y,
              width: selectionBox.width,
              height: selectionBox.height,
            }}
          />
        )}

        {treeReady && nodes.length === 0 && (useDemo || !docsLoading) && (
          <AtlasEmpty useDemo={useDemo} onCreateRootFolder={handleCreateRootFolder} />
        )}
      </div>

      <AtlasMinimap
        nodes={nodes}
        edges={edges}
        bounds={bounds}
        pan={pan}
        zoom={zoom}
        containerRef={containerRef}
        liveApplyRef={minimapApplyRef}
        onPanChange={setPanLive}
        onPanChangeEnd={endPanLive}
      />

      <AtlasContextMenu
        ctx={ctx}
        useDemo={useDemo}
        selectedCount={
          ctx && selectedIds.has(ctx.folderId) && !isAtlasUnfiledId(ctx.folderId)
            ? selectedIds.size
            : 1
        }
        isUnfiled={ctx ? isAtlasUnfiledId(ctx.folderId) : false}
        onCreateRootFolder={handleCreateRootFolder}
        onCreateChild={handleCreateChild}
        onCreateDoc={handleCreateDocInFolder}
        onPasteDoc={handlePasteDoc}
        onRename={handleRename}
        onBrowseFolder={handleOpenBrowse}
        onDelete={handleDeleteFromMenu}
        onClose={() => setCtx(null)}
      />

      <DeleteFoldersModal
        isOpen={!!pending}
        info={pending}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default AtlasView;
